import {
  ArrowLeft,
  CheckCheck,
  ClipboardList,
  CreditCard,
  MessageSquareText,
  Send,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supportChatApi, type SupportChatEvent, type SupportChatMessage } from "@/api";
import { env } from "@/config/env";
import { auth } from "@/lib/auth";

type MessageFrom = "visitor" | "service";

interface ChatMessage {
  id: number;
  from: MessageFrom;
  text: string;
  time: string;
}

const SERVICE_AVATAR =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96">
      <defs>
        <linearGradient id="bg" x1="12" x2="84" y1="8" y2="88" gradientUnits="userSpaceOnUse">
          <stop stop-color="#F4D8DE"/>
          <stop offset="0.55" stop-color="#EAD3E4"/>
          <stop offset="1" stop-color="#C7D3F4"/>
        </linearGradient>
      </defs>
      <rect width="96" height="96" rx="48" fill="url(#bg)"/>
      <circle cx="48" cy="39" r="18" fill="#FFF4EC"/>
      <path d="M26 78c4-14 13-22 22-22s18 8 22 22" fill="#fff"/>
      <path d="M35 34c2-9 8-14 15-13 8 1 13 6 14 15-6-4-12-4-17-1-5 2-9 2-12-1Z" fill="#8F3F51"/>
      <path d="M42 47c3 2 9 2 12 0" stroke="#C97585" stroke-width="2.5" stroke-linecap="round"/>
      <path d="M67 40c6 1 10 5 10 11s-4 10-10 11" fill="none" stroke="#7A93E4" stroke-width="4" stroke-linecap="round"/>
      <path d="M29 40c-6 1-10 5-10 11s4 10 10 11" fill="none" stroke="#7A93E4" stroke-width="4" stroke-linecap="round"/>
    </svg>
  `);

const QUICK_BUTTONS = [
  { icon: ClipboardList, label: "我要咨询订单" },
  { icon: CreditCard, label: "退款怎么处理" },
  { icon: MessageSquareText, label: "技师迟到了" },
];

const SUPPORT_CHAT_UNREAD_REFRESH_EVENT = "athome:support-chat-unread-refresh";

const emitUnreadRefresh = () => {
  window.dispatchEvent(new Event(SUPPORT_CHAT_UNREAD_REFRESH_EVENT));
};

const toDisplayTime = (value?: string) => {
  if (!value) return "";
  const date = new Date(`${value}${value.endsWith("Z") ? "" : "Z"}`);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const toChatMessage = (message: SupportChatMessage): ChatMessage => {
  return {
    id: message.id,
    from: message.senderType === "user" ? "visitor" : "service",
    text: message.content,
    time: toDisplayTime(message.createdAt),
  };
};

const mergeMessages = (current: ChatMessage[], incoming: ChatMessage[]) => {
  const map = new Map<number, ChatMessage>();
  [...current, ...incoming].forEach((item) => map.set(item.id, item));
  return Array.from(map.values()).sort((a, b) => a.id - b.id);
};

const isSupportChatEvent = (payload: SupportChatEvent) => {
  return typeof payload.type === "string" && payload.type.startsWith("support.");
};

const buildWsUrl = () => {
  const token = auth.getToken();
  if (!token || typeof window === "undefined") return "";
  const base = env.apiBaseUrl.startsWith("http")
    ? env.apiBaseUrl
    : `${window.location.origin}${env.apiBaseUrl}`;
  return `${base.replace(/^http/i, "ws")}/support-chat/ws?token=${encodeURIComponent(token)}`;
};

export default function SupportChatPage() {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const conversationIdRef = useRef<number | null>(null);
  const latestMessageIdRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);
  const fetchingMessagesRef = useRef(false);
  const markingReadRef = useRef(false);
  const [inputText, setInputText] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const appendMessages = useCallback((records: SupportChatMessage[]) => {
    if (!records.length) return;
    latestMessageIdRef.current = Math.max(latestMessageIdRef.current, ...records.map((item) => item.id));
    setMessages((items) => mergeMessages(items, records.map(toChatMessage)));
  }, []);

  const markConversationRead = useCallback(async (conversationId: number) => {
    if (markingReadRef.current) return;
    markingReadRef.current = true;
    try {
      await supportChatApi.markRead(conversationId).catch(() => undefined);
      emitUnreadRefresh();
    } finally {
      markingReadRef.current = false;
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    const conversationId = conversationIdRef.current;
    if (!conversationId || fetchingMessagesRef.current) return;
    fetchingMessagesRef.current = true;
    try {
      const page = await supportChatApi.getMessages({
        conversationId,
        afterMessageId: latestMessageIdRef.current || undefined,
        pageNo: 1,
        pageSize: 50,
      });
      const records = page.records || [];
      appendMessages(records);
      if (records.some((item) => item.senderType === "admin" || item.senderType === "system")) {
        await markConversationRead(conversationId);
      }
    } finally {
      fetchingMessagesRef.current = false;
    }
  }, [appendMessages, markConversationRead]);

  useEffect(() => {
    let disposed = false;
    const initialize = async () => {
      try {
        const conversation = await supportChatApi.openConversation();
        if (disposed) return;
        conversationIdRef.current = conversation.id;
        latestMessageIdRef.current = 0;
        const page = await supportChatApi.getMessages({ conversationId: conversation.id, pageNo: 1, pageSize: 50 });
        if (disposed) return;
        appendMessages(page.records || []);
        await markConversationRead(conversation.id);
      } catch (error) {
        if (disposed) return;
        setMessages([
          {
            id: Date.now(),
            from: "service",
            text: error instanceof Error ? error.message : "在线客服暂时无法接入，请稍后再试。",
            time: "",
          },
        ]);
      }
    };
    initialize();
    return () => {
      disposed = true;
    };
  }, [appendMessages]);

  useEffect(() => {
    const wsUrl = buildWsUrl();
    if (!wsUrl) return;
    let disposed = false;
    let socket: WebSocket | null = null;

    const clearTimers = () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (heartbeatTimerRef.current) {
        window.clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };

    const connectSocket = () => {
      if (disposed) return;
      socket = new WebSocket(wsUrl);
      socket.onopen = () => {
        if (disposed) {
          socket?.close();
          return;
        }
        if (heartbeatTimerRef.current) {
          window.clearInterval(heartbeatTimerRef.current);
        }
        heartbeatTimerRef.current = window.setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send("ping");
          }
        }, 25000);
      };
      socket.onmessage = (event) => {
        if (disposed) return;
        try {
          const payload = JSON.parse(event.data) as SupportChatEvent;
          if (!isSupportChatEvent(payload)) {
            return;
          }
          if (payload.type === "support.pong") {
            return;
          }
          if (payload.message) {
            appendMessages([payload.message]);
            if (payload.message.senderType === "admin" && conversationIdRef.current) {
              markConversationRead(conversationIdRef.current).catch(() => undefined);
            }
          }
        } catch {
          return;
        }
      };
      socket.onclose = () => {
        if (heartbeatTimerRef.current) {
          window.clearInterval(heartbeatTimerRef.current);
          heartbeatTimerRef.current = null;
        }
        if (!disposed && !reconnectTimerRef.current) {
          reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;
            connectSocket();
          }, 3000);
        }
      };
      socket.onerror = () => {
        socket?.close();
      };
    };

    connectSocket();
    return () => {
      disposed = true;
      clearTimers();
      socket?.close();
    };
  }, [appendMessages, fetchMessages]);

  const handleSend = useCallback(
    (content?: string) => {
      const text = (content ?? inputText).trim();
      if (!text || isTyping) return;
      const conversationId = conversationIdRef.current;
      if (!conversationId) {
        setMessages((items) =>
          mergeMessages(items, [
            {
              id: Date.now(),
              from: "service",
              text: "客服连接中，请稍后再试。",
              time: "",
            },
          ]),
        );
        return;
      }
      setInputText("");
      setIsTyping(true);
      supportChatApi
        .sendMessage({
          conversationId,
          content: text,
          clientMsgId: `customer-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        })
        .then((message) => {
          appendMessages([message]);
          fetchMessages().catch(() => undefined);
        })
        .catch((error) => {
          setMessages((items) =>
            mergeMessages(items, [
              {
                id: Date.now(),
                from: "service",
                text: error instanceof Error ? error.message : "消息发送失败，请稍后重试。",
                time: "",
              },
            ]),
          );
        })
        .finally(() => setIsTyping(false));
      inputRef.current?.focus();
    },
    [appendMessages, fetchMessages, inputText, isTyping],
  );

  return (
    <div
      className="mx-auto flex h-[100dvh] max-w-[480px] flex-col overflow-hidden text-[#2D1F24]"
      style={{ background: "#F5F0EE" }}
    >
      <header
        className="flex flex-shrink-0 items-center gap-3 px-4 py-2.5"
        style={{ background: "#fff", borderBottom: "1px solid rgba(233,225,228,0.8)" }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full transition-all active:scale-90"
          style={{ background: "#F2EBF0", color: "#6B4F58" }}
          aria-label="返回"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="relative flex-shrink-0">
          <div className="h-9 w-9 overflow-hidden rounded-full" style={{ border: "2px solid #E9E1E4" }}>
            <img src={SERVICE_AVATAR} alt="客服" className="h-full w-full object-cover object-top" />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold leading-tight" style={{ color: "#2D1F24" }}>
            在线客服
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-[10px]">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#22C55E" }} />
            <span style={{ color: "#8A7A80" }}>在线中 · 平均 1 分钟内回复</span>
          </p>
        </div>

        <span
          className="flex-shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-medium"
          style={{ background: "#F2EBF0", color: "#A88992" }}
        >
          在线
        </span>
      </header>

      <main ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        <section
          className="mb-2 rounded-2xl px-4 py-3.5"
          style={{ background: "#fff", border: "1px solid rgba(233,225,228,0.8)" }}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[15px] font-bold" style={{ color: "#2D1F24" }}>
                平台客服支持
              </p>
              <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "#8A7A80" }}>
                优先处理预约、订单、退款和服务体验相关问题。
              </p>
            </div>
            <span
              className="flex-shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-medium"
              style={{ background: "#FAE8EB", color: "#C97585" }}
            >
              预计 1 分钟
            </span>
          </div>
        </section>

        <div className="text-center">
          <span
            className="inline-block rounded-full px-3 py-1 text-[10px]"
            style={{ background: "rgba(200,180,186,0.25)", color: "#9A8A90" }}
          >
            当前会话由平台在线客服接入，消息会实时同步
          </span>
        </div>

        <div className="py-1 text-center">
          <span
            className="rounded-full px-3 py-0.5 text-[10px]"
            style={{ background: "rgba(200,180,186,0.2)", color: "#A8999E" }}
          >
            今天
          </span>
        </div>

        {messages.map((message) => {
          const isVisitor = message.from === "visitor";
          return (
            <div key={message.id} className={`flex ${isVisitor ? "justify-end" : "justify-start"}`}>
              {!isVisitor && (
                <div
                  className="mr-2 h-8 w-8 flex-shrink-0 self-end overflow-hidden rounded-full"
                  style={{ border: "1.5px solid #E9E1E4" }}
                >
                  <img src={SERVICE_AVATAR} alt="客服" className="h-full w-full object-cover object-top" />
                </div>
              )}
              <div className="max-w-[78%]">
                <div
                  className="px-3.5 py-2.5 text-[14px] leading-relaxed"
                  style={{
                    background: isVisitor ? "linear-gradient(135deg,#6B3A4F,#8B5A6F)" : "#fff",
                    color: isVisitor ? "#fff" : "#2D1F24",
                    borderRadius: isVisitor ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    boxShadow: isVisitor ? "0 2px 8px rgba(107,58,79,0.2)" : "0 1px 4px rgba(45,31,36,0.06)",
                    border: isVisitor ? "none" : "1px solid rgba(233,225,228,0.8)",
                  }}
                >
                  {message.text}
                </div>
                <p
                  className="mt-1 flex items-center gap-1 text-[10px]"
                  style={{
                    color: "#B8A8AE",
                    justifyContent: isVisitor ? "flex-end" : "flex-start",
                    paddingRight: isVisitor ? 4 : 0,
                    paddingLeft: isVisitor ? 0 : 4,
                  }}
                >
                  {message.time}
                  {isVisitor && <CheckCheck size={11} color="#C8B4BA" />}
                </p>
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div className="flex justify-start">
            <div
              className="mr-2 h-8 w-8 flex-shrink-0 self-end overflow-hidden rounded-full"
              style={{ border: "1.5px solid #E9E1E4" }}
            >
              <img src={SERVICE_AVATAR} alt="客服" className="h-full w-full object-cover object-top" />
            </div>
            <div
              className="flex items-center gap-1 px-3.5 py-2.5"
              style={{
                background: "#fff",
                border: "1px solid rgba(233,225,228,0.8)",
                borderRadius: "18px 18px 18px 4px",
                boxShadow: "0 1px 4px rgba(45,31,36,0.06)",
              }}
            >
              {[0, 150, 300].map((delay) => (
                <span
                  key={delay}
                  className="h-1.5 w-1.5 animate-bounce rounded-full"
                  style={{ background: "#C8B4BA", animationDelay: `${delay}ms` }}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      <section className="flex-shrink-0 px-4 pb-2 pt-2" style={{ background: "#F5F0EE" }}>
        <div className="flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          {QUICK_BUTTONS.map((button) => {
            const Icon = button.icon;
            return (
              <button
                key={button.label}
                type="button"
                onClick={() => handleSend(button.label)}
                disabled={isTyping}
                className="flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-[12px] transition-all active:scale-95 disabled:opacity-50"
                style={{
                  background: "#fff",
                  border: "1px solid rgba(200,180,186,0.5)",
                  color: "#6B4F58",
                  boxShadow: "0 1px 3px rgba(45,31,36,0.04)",
                }}
              >
                <Icon size={14} color="#A88992" />
                {button.label}
              </button>
            );
          })}
        </div>
      </section>

      <footer
        className="flex flex-shrink-0 items-center gap-2.5 px-4 py-3"
        style={{ background: "#fff", borderTop: "1px solid rgba(233,225,228,0.8)" }}
      >
        <div className="flex h-12 flex-1 items-center rounded-2xl px-3.5" style={{ background: "#F5F0EE" }}>
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={(event) => setInputText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
            placeholder="输入要咨询的问题"
            rows={1}
            maxLength={500}
            className="h-6 w-full resize-none bg-transparent text-[14px] leading-6 outline-none"
            style={{ color: "#2D1F24", caretColor: "#C97585" }}
          />
        </div>
        <button
          type="button"
          onClick={() => handleSend()}
          disabled={!inputText.trim() || isTyping}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-all active:scale-90 disabled:opacity-30"
          style={{
            background: inputText.trim() && !isTyping ? "linear-gradient(135deg,#6B3A4F,#8B5A6F)" : "#E9E1E4",
            boxShadow: inputText.trim() && !isTyping ? "0 2px 8px rgba(107,58,79,0.25)" : "none",
          }}
          aria-label="发送"
        >
          <Send size={16} color={inputText.trim() && !isTyping ? "#fff" : "#B8A8AE"} />
        </button>
      </footer>

      <div className="flex-shrink-0" style={{ height: "env(safe-area-inset-bottom)" }} />
    </div>
  );
}
